import { useState } from "react";
import { PlayCircle, Layers, Clock3, ChevronDown, ArrowUpRight, CalendarDays, Info } from "lucide-react";
import dayjs from "dayjs";
import { Reveal, Eyebrow } from "./Reveal";
import { ProPractBadge } from "./ProPractBadge";
import { EnrollModal } from "./EnrollModal";
import { stripHtml } from "./RichText";
import { useCatalog } from "@/lib/site";

const CourseCard = ({ c, i, onDetails, prefix }) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={i * 0.12}>
      <article
        data-testid={`${prefix}-card-${i}`}
        className="group flex h-full flex-col overflow-hidden rounded-3xl border border-ink/10 bg-white transition-all duration-500 hover:-translate-y-1.5 hover:border-terra/40 hover:shadow-deep"
      >
        <div className="relative bg-cream p-8">
          {c.thumbnail ? (
            <img src={c.thumbnail} alt={c.title} className="mb-4 aspect-video w-full rounded-xl object-cover" loading="lazy" />
          ) : (
            <PlayCircle className="h-10 w-10 text-terra transition-transform duration-500 group-hover:scale-110" />
          )}
          {c.kind === "event" && c.dates?.length > 0 && (
            <span className="absolute right-6 top-6 inline-flex items-center gap-1.5 rounded-full bg-terra px-3 py-1 font-outfit text-[11px] font-semibold uppercase tracking-wider text-white">
              <CalendarDays className="h-3 w-3" />
              {dayjs(c.dates[0].start || c.dates[0].date || c.dates[0]).format("D MMM")}
            </span>
          )}
          <h3 className="mt-5 font-display text-2xl font-semibold leading-snug text-ink">{c.title}</h3>
          {c.tagline && <p className="mt-1 text-sm italic text-faint line-clamp-2">{stripHtml(c.tagline)}</p>}
        </div>
        <div className="flex flex-1 flex-col p-8 pt-6">
          <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs font-outfit text-plumsoft">
            {!!c.modules && (
              <span className="inline-flex items-center gap-1.5">
                <Layers className="h-3.5 w-3.5 text-terra" /> {c.modules} modules
              </span>
            )}
            {!!c.hours && (
              <span className="inline-flex items-center gap-1.5">
                <Clock3 className="h-3.5 w-3.5 text-terra" /> {c.hours}
              </span>
            )}
            {c.kind === "event" && c.event_mode && (
              <span className="inline-flex items-center gap-1.5 capitalize">{c.event_mode}</span>
            )}
          </div>
          {(c.why || c.who) && (
            <div className="mt-4">
              <button
                data-testid={`${prefix}-why-toggle-${i}`}
                onClick={() => setOpen((v) => !v)}
                className="inline-flex items-center gap-1.5 font-outfit text-xs font-semibold uppercase tracking-wider text-terra"
              >
                Why & who should buy
                <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div data-testid={`${prefix}-why-panel-${i}`} className="mt-3 space-y-2 rounded-xl bg-cream p-4 text-xs leading-relaxed text-plumsoft">
                  {c.why && <p><span className="font-semibold text-terra">Why: </span>{c.why}</p>}
                  {c.who && <p><span className="font-semibold text-terra">Who: </span>{c.who}</p>}
                </div>
              )}
            </div>
          )}
          <div className="mt-auto flex items-center justify-between gap-3 border-t border-ink/10 pt-5 mt-6">
            <p className="font-display text-2xl font-semibold text-ink">{c.price}</p>
            <div className="flex items-center gap-2">
              <button
                data-testid={`${prefix}-details-btn-${i}`}
                onClick={() => onDetails(c)}
                className="inline-flex items-center gap-1.5 rounded-full border border-ink/20 px-4 py-2.5 font-outfit text-sm font-semibold text-ink transition-colors duration-300 hover:border-terra hover:text-terra"
              >
                <Info className="h-4 w-4" /> Details
              </button>
              <button
                data-testid={`${prefix}-enroll-btn-${i}`}
                onClick={() => onDetails(c, true)}
                className="inline-flex items-center gap-1.5 rounded-full bg-ink px-4 py-2.5 font-outfit text-sm font-semibold text-sand transition-colors duration-300 hover:bg-terra"
              >
                Enroll now <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </article>
    </Reveal>
  );
};

export const Courses = () => {
  const { courses, liveEvents } = useCatalog();
  const [viewing, setViewing] = useState(null);

  return (
    <section id="courses" data-testid="courses-section" className="py-20 sm:py-28 lg:py-36">
      <div className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16">
        <Reveal className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <Eyebrow>Learn with Yogyata</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-ink">
              Courses & <span className="italic text-terra">live workshops.</span>
            </h2>
          </div>
          <ProPractBadge />
        </Reveal>

        {liveEvents.length > 0 && (
          <>
            <Reveal className="mt-14">
              <p className="font-outfit text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-terra" data-testid="events-subheading">
                Upcoming live events & workshops
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="live-events-grid">
              {liveEvents.map((c, i) => (
                <CourseCard key={c.id || c.event_id || i} c={c} i={i} onDetails={setViewing} prefix="event" />
              ))}
            </div>
          </>
        )}

        {courses.length > 0 && (
          <>
            <Reveal className="mt-14">
              <p className="font-outfit text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-sage" data-testid="courses-subheading">
                Self-paced courses
              </p>
            </Reveal>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3" data-testid="courses-grid">
              {courses.map((c, i) => (
                <CourseCard key={c.id || c.event_id || i} c={c} i={i} onDetails={setViewing} prefix="course" />
              ))}
            </div>
          </>
        )}
      </div>
      <EnrollModal event={viewing} open={!!viewing} onClose={() => setViewing(null)} />
    </section>
  );
};
